import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Copy, Check, Link2, Mail } from "lucide-react";
import { usePortalAuth } from "../../lib/portalAuth";
import { OW_API, formatApiErrorDetail } from "../../lib/operatorWalk";

const EXPIRY_OPTIONS = [7, 14, 30, 60, 90];

export default function ShareModal({ report, onClose, onCreated }) {
  const { authHeader } = usePortalAuth();
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [expiresDays, setExpiresDays] = useState(30);
  const [emailTo, setEmailTo] = useState("");
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${OW_API}/portal/reports/${report.id}/share`,
        {
          recipient_name: recipientName.trim(),
          message: message.trim(),
          expires_days: expiresDays,
          email_to: emailTo.trim() || null,
        },
        { headers: authHeader }
      );
      setShareUrl(res.data.url);
      if (res.data.emailed) toast.success(`Link emailed to ${res.data.emailed}.`);
      else toast.success("Share link generated.");
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate link.");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed. Select and copy the link manually.");
    }
  };

  const inputCls = "w-full border border-brand-200 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-900/50 px-4 py-8 overflow-y-auto" data-testid="share-modal" onClick={onClose}>
      <div className="bg-white border border-brand-200 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-100">
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-brand-500">Share report</div>
            <h3 className="font-serif text-xl text-brand-700">{report.month_label}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-ink-muted hover:text-brand-700" data-testid="share-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!shareUrl ? (
            <>
              <p className="text-sm text-ink-soft leading-relaxed">
                Generate a private, read-only link to this report. Recipients do not need an account. You can revoke
                the link at any time.
              </p>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Recipient name (optional)</label>
                <input className={`mt-1.5 ${inputCls}`} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="share-recipient-name" placeholder="e.g. Jane, board member" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Personal message (optional)</label>
                <textarea className={`mt-1.5 ${inputCls}`} rows={3} maxLength={500} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="share-message-input" placeholder="A short note for the recipient." />
                <div className="text-[11px] text-ink-muted text-right mt-1">{message.length}/500</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Link expires in</label>
                <select className={`mt-1.5 ${inputCls}`} value={expiresDays} onChange={(e) => setExpiresDays(Number(e.target.value))} data-testid="share-expiry">
                  {EXPIRY_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">Email it directly (optional)</label>
                <input className={`mt-1.5 ${inputCls}`} value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="share-email-to" placeholder="recipient@example.com" />
              </div>
              <button
                onClick={generate}
                disabled={generating}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="share-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Generate link
              </button>
            </>
          ) : (
            <div className="space-y-4" data-testid="share-result">
              <p className="text-sm text-ink-soft">Your link is ready. It expires in {expiresDays} days.</p>
              <div className="flex items-stretch gap-2">
                <input readOnly value={shareUrl} className={`${inputCls} bg-brand-100`} data-testid="share-url-output" onFocus={(e) => e.target.select()} />
                <button onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white px-4 text-sm font-medium transition-colors" data-testid="share-copy-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              {emailTo && (
                <div className="inline-flex items-center gap-2 text-sm text-ink-muted">
                  <Mail className="w-4 h-4 text-brand-400" /> Also emailed to {emailTo}
                </div>
              )}
              <button onClick={onClose} className="w-full border border-brand-200 text-brand-700 hover:bg-brand-100 px-6 py-3 text-sm font-medium transition-colors" data-testid="share-done-btn">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
